/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ExtensionSelect.tsx
 🗓️ Created: 2025-01-10
 🧩 Description:
    Custom extension selection component with server-side search functionality,
    for selecting extensions efficiently even with thousands of extensions.

 🔧 Notes:
    - Implements searchable dropdown with server-side search
    - Supports keyboard navigation (arrow keys, enter, escape)
    - Shows extension name and number
    - Optimized for performance with server-side filtering
    - Integrates with form validation systems

 🔁 Last Updated: 2025-01-10 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo, useCallback, useEffect, memo } from 'react';
import { ChevronDown, Search, Phone, X } from 'lucide-react';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { useI18n } from '../../hooks/useI18n';

interface ExtensionOption {
  id: string;
  name: string;
  extension: string;
  description?: string;
}

interface ExtensionSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  valProperty?: 'id' | 'extension';
}

export const ExtensionSelect: React.FC<ExtensionSelectProps> = ({
  value,
  onChange,
  className = '',
  placeholder,
  disabled = false,
  required = false,
  valProperty = 'extension'
}) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [extensions, setExtensions] = useState<ExtensionOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedExtension, setSelectedExtension] = useState<ExtensionOption | null>(null);

  // Load extensions from server
  const loadExtensions = useCallback(async (search: string = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '50',
        sortBy: 'extension',
        sortOrder: 'asc',
      });

      if (search.trim()) {
        params.append('search', search.trim());
      }

      const response = await fetch(
        buildApiUrl(`/api/telephony/extensions?${params}`),
        {
          headers: getApiHeaders()
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const mapped = result.data.map((ext: any) => ({
            id: ext.id?.toString() || ext.extension, // Use database ID, not extension number
            name: `${ext.extension}${ext.name ? ` - ${ext.name}` : ''}`,
            extension: ext.extension,
            description: ext.name || ''
          }));
          setExtensions(mapped);
        }
      }
    } catch (error) {
      console.error('Failed to load extensions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load initial extensions when dropdown opens
  useEffect(() => {
    if (isOpen) {
      loadExtensions(searchQuery);
    }
  }, [isOpen, loadExtensions]);

  // Load selected extension if value exists
  useEffect(() => {
    if (value && !selectedExtension) {
      const loadSelected = async () => {
        try {
          if (valProperty === 'id') {
            // Search by database ID (or UUID)
            const response = await fetch(
              buildApiUrl(`/api/telephony/extensions/${value}`),
              { headers: getApiHeaders() }
            );

            if (response.ok) {
              const result = await response.json();
              if (result.success && result.data) {
                const ext = result.data;
                setSelectedExtension({
                  id: ext.id?.toString() || ext.extension,
                  name: `${ext.extension}${ext.name ? ` - ${ext.name}` : ''}`,
                  extension: ext.extension,
                  description: ext.name || ''
                });
              }
            }
          } else {
            // Search by extension number
            const params = new URLSearchParams({
              page: '1',
              limit: '1',
              search: value,
              sortBy: 'extension',
              sortOrder: 'asc',
            });

            const response = await fetch(
              buildApiUrl(`/api/telephony/extensions?${params}`),
              { headers: getApiHeaders() }
            );

            if (response.ok) {
              const result = await response.json();
              if (result.success && Array.isArray(result.data) && result.data.length > 0) {
                // Find exact match
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const ext = result.data.find((e: any) => e.extension === value) || result.data[0];
                setSelectedExtension({
                  id: ext.id?.toString() || ext.extension,
                  name: `${ext.extension}${ext.name ? ` - ${ext.name}` : ''}`,
                  extension: ext.extension,
                  description: ext.name || ''
                });
              }
            }
          }
        } catch (error) {
          console.error('Failed to load selected extension:', error);
        }
      };

      loadSelected();
    }
  }, [value, selectedExtension]);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      loadExtensions(searchQuery);
    }, searchQuery ? 300 : 0);

    return () => clearTimeout(timer);
  }, [searchQuery, isOpen, loadExtensions]);

  // Filter options based on search (client-side filtering for already loaded items)
  const filteredExtensions = useMemo(() => {
    if (!searchQuery.trim()) {
      return extensions;
    }

    const query = searchQuery.toLowerCase();
    return extensions.filter(ext =>
      ext.name.toLowerCase().includes(query) ||
      ext.extension.toLowerCase().includes(query) ||
      (ext.description && ext.description.toLowerCase().includes(query))
    );
  }, [searchQuery, extensions]);

  // Get selected label
  const selectedLabel = useMemo(() => {
    if (selectedExtension) {
      return selectedExtension.name;
    }
    if (value) {
      return value;
    }
    return placeholder || t('agents.form.fields.extension.selectPlaceholder');
  }, [value, placeholder, selectedExtension, t]);

  // Handle selection
  const handleSelect = useCallback((extension: ExtensionOption) => {
    // Pass the correct property based on configuration
    onChange(valProperty === 'id' ? extension.id : extension.extension);
    setSelectedExtension(extension);
    setIsOpen(false);
    setSearchQuery('');
  }, [onChange, valProperty]);

  // Handle keyboard
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  }, []);

  return (
    <div className="relative">
      {/* Trigger Area */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            !disabled && setIsOpen(!isOpen);
          } else {
            handleKeyDown(e);
          }
        }}
        className={`
          w-full px-3 py-2 text-sm border rounded bg-background text-foreground 
          focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent
          flex items-center justify-between gap-2
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}
          ${isOpen ? 'ring-1 ring-primary border-transparent' : 'border-border'}
          ${className}
        `}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="truncate text-left select-none">
            {loading ? t('common.loading') : selectedLabel}
          </span>
        </div>
        
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {value && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
                setSelectedExtension(null);
                setIsOpen(false);
              }}
              className="p-0.5 rounded-full hover:bg-muted-foreground/20 text-muted-foreground hover:text-foreground transition-colors outline-none focus:ring-1 focus:ring-primary"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder={t('agents.form.fields.extension.searchPlaceholder') || 'Search extensions...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                {t('common.loading')}
              </div>
            ) : filteredExtensions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                {t('agents.form.fields.extension.noExtensionsFound') || 'No extensions found'}
              </div>
            ) : (
              filteredExtensions.map((ext) => (
                <button
                  key={ext.id}
                  type="button"
                  onClick={() => handleSelect(ext)}
                  className={`
                    w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                    flex items-center gap-2
                    ${value === ext.extension ? 'bg-primary/10 text-primary' : 'text-foreground'}
                  `}
                >
                  <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate font-medium">{ext.name}</span>
                    {ext.description && ext.id !== ext.description && (
                      <span className="text-xs text-muted-foreground truncate">{ext.description}</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer with count */}
          {!loading && filteredExtensions.length > 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border bg-muted/30">
              {filteredExtensions.length} {t('agents.form.fields.extension.extensionsFound') || 'extensions found'}
            </div>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsOpen(false);
            setSearchQuery('');
          }}
        />
      )}
    </div>
  );
};

export default memo(ExtensionSelect);

